import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";
import { syncDayjsLocale } from "../lib/dayjsLocale";
import {
	applyDocumentDirection,
	isSupportedLanguage,
	SUPPORTED_LANGUAGES,
	type SupportedLanguage,
	toSupportedLanguage,
} from "../lib/languageGeo";
import { isCustomerFacingAppPath, isSalesSurface } from "../lib/surface";
import en from "../locales/en.json";
import enExtra from "../locales/extras/en.json";

// POC-858: ONLY English is statically bundled. The non-English catalogs
// (~200KB of JSON each) used to be static imports for the dashboard init
// below, which put them in main-*.js that every buyer downloads before
// /checkout can paint. Each language now loads on demand through
// ensureLanguageBundle() (import.meta.glob → one async chunk per language).

/**
 * App i18n (POC-655 / POC-407).
 *
 * - **Sales / checkout (buyer surface):** always English. Auto IP/browser
 *   switching previously showed Arabic checkout chrome to buyers who don't
 *   read Arabic (POC-407). That lock stays.
 * - **App-origin customer routes** (`/verify-buyer`, `/invoice`, `/portal`):
 *   also English-only — these mount on `app.*` but are buyer-facing.
 * - **Dashboard (creators):** all 10 locales. Language changes only via
 *   explicit choice (settings, sidebar, onboarding popup) or a saved backend
 *   preference — never a silent IP/navigator switch.
 *
 * Non-English catalogs are loaded asynchronously on the dashboard so the
 * sales/checkout entry graph does not pay for ~2MB of unused locale JSON.
 */

const USER_LANG_KEY = "preferred-language";
const USER_EXPLICIT_KEY = "preferred-language-explicit";

const englishLocked =
	typeof window !== "undefined"
		? isSalesSurface() || isCustomerFacingAppPath()
		: false;

function deepMerge(
	base: Record<string, unknown>,
	extra: Record<string, unknown>,
): Record<string, unknown> {
	const out: Record<string, unknown> = { ...base };
	for (const [key, value] of Object.entries(extra)) {
		const current = out[key];
		if (
			value &&
			typeof value === "object" &&
			!Array.isArray(value) &&
			current &&
			typeof current === "object" &&
			!Array.isArray(current)
		) {
			out[key] = deepMerge(
				current as Record<string, unknown>,
				value as Record<string, unknown>,
			);
		} else {
			out[key] = value;
		}
	}
	return out;
}

const enBundle = {
	translation: deepMerge(en as Record<string, unknown>, enExtra),
};

const localStorageDetector = {
	name: "localStorage",
	lookup(): string | undefined {
		if (englishLocked) return "en";
		try {
			const explicit = localStorage.getItem(USER_LANG_KEY);
			if (isSupportedLanguage(explicit)) return explicit;
		} catch {
			// ignore
		}
		return undefined;
	},
	cacheUserLanguage(lng: string) {
		if (englishLocked) return;
		try {
			if (
				localStorage.getItem(USER_EXPLICIT_KEY) === "true" &&
				isSupportedLanguage(lng)
			) {
				localStorage.setItem(USER_LANG_KEY, lng);
			}
		} catch {
			// ignore
		}
	},
};

// Every non-English catalog is its own async chunk, loaded only when that
// language is picked (POC-858: buyers never download them).
type JsonModule =
	| { default?: Record<string, unknown> }
	| Record<string, unknown>;
const baseCatalogs = import.meta.glob<JsonModule>([
	"../locales/*.json",
	"!../locales/en.json",
]);
const extraCatalogs = import.meta.glob<JsonModule>([
	"../locales/extras/*.json",
	"!../locales/extras/en.json",
]);
// Release notes (What's new + /changelog); English is the fallback written in
// src/data/releaseNotes.ts, so only other languages load a file.
const changelogCatalogs = import.meta.glob<JsonModule>([
	"../locales/changelog/*.json",
	"!../locales/changelog/en.json",
]);

const asRecord = (mod: JsonModule | undefined): Record<string, unknown> => {
	if (!mod) return {};
	if ("default" in mod && mod.default) {
		return mod.default as Record<string, unknown>;
	}
	return mod as Record<string, unknown>;
};

/** Load and register one language's catalog (base + extras + changelog). */
async function ensureLanguageBundle(lng: string): Promise<void> {
	if (lng === "en" || i18next.hasResourceBundle(lng, "translation")) return;
	try {
		const loadBase = baseCatalogs[`../locales/${lng}.json`];
		if (!loadBase) return;
		const loadExtra = extraCatalogs[`../locales/extras/${lng}.json`];
		const loadChangelog = changelogCatalogs[`../locales/changelog/${lng}.json`];
		const [base, extra, changelog] = await Promise.all([
			loadBase(),
			loadExtra?.(),
			loadChangelog?.(),
		]);
		i18next.addResourceBundle(
			lng,
			"translation",
			deepMerge(
				deepMerge(asRecord(base), asRecord(extra)),
				asRecord(changelog),
			),
			true,
			true,
		);
	} catch {
		// ignore — the dashboard falls back to English
	}
}

/** The supported language the UI is in now ("zh-Hant", "pt", …). */
export function currentLanguage(): SupportedLanguage {
	return toSupportedLanguage(i18next.language) ?? "en";
}

/**
 * Settles once the stored language's catalog (and dayjs locale) is in. The
 * dashboard waits for it before its first render, so nothing is rendered in
 * English and then kept in state (src/index.tsx).
 */
let boot: Promise<unknown> = Promise.resolve();

if (englishLocked) {
	void i18next.use(initReactI18next).init({
		resources: { en: enBundle },
		lng: "en",
		fallbackLng: "en",
		supportedLngs: ["en"],
		interpolation: { escapeValue: false },
	});
} else {
	const languageDetector = new LanguageDetector();
	languageDetector.addDetector(localStorageDetector);

	boot = i18next
		.use(languageDetector)
		.use(initReactI18next)
		.init({
			// POC-858: init with English only so main-*.js stays lean for
			// buyers; non-English dashboard catalogs register async below.
			resources: { en: enBundle },
			lng: "en",
			fallbackLng: "en",
			supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
			nonExplicitSupportedLngs: true,
			// Explicit preference only — never silent navigator/IP auto-switch.
			detection: {
				order: ["localStorage"],
				caches: ["localStorage"],
			},
			interpolation: {
				escapeValue: false,
			},
		})
		.then(async () => {
			applyDocumentDirection(currentLanguage());
			// The detector may restore the language before the listener below
			// exists. Dates already rendered in English, so once dayjs has the
			// language, ask react-i18next to re-render.
			const restored = currentLanguage();
			if (restored !== "en") {
				void syncDayjsLocale(restored).then(() => {
					if (currentLanguage() === restored) {
						i18next.emit("languageChanged", i18next.language);
					}
				});
			}
			// POC-858: only the stored preference's catalog loads, after
			// boot (an async chunk, never in the buyer entry).
			const preferred = getStoredLanguage();
			if (preferred && preferred !== currentLanguage()) {
				// Catalog and dayjs locale first, so the re-render that
				// changeLanguage triggers already has both.
				await Promise.all([
					ensureLanguageBundle(preferred),
					syncDayjsLocale(preferred),
				]);
				await i18next.changeLanguage(preferred);
			}
		});

	i18next.on("languageChanged", (lng) => {
		const language = toSupportedLanguage(lng) ?? "en";
		applyDocumentDirection(language);
		void syncDayjsLocale(language);
	});
}

/** Resolves when the stored language is ready, or after `timeoutMs`. */
export function languageReady(timeoutMs = 2000): Promise<void> {
	return Promise.race([
		boot.then(
			() => undefined,
			() => undefined,
		),
		new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
	]);
}

/**
 * Mark a language change as an explicit user choice and apply it.
 * No-op on English-locked buyer surfaces (POC-407).
 */
export async function setUserLanguage(lng: string): Promise<void> {
	if (englishLocked) return;
	if (!isSupportedLanguage(lng)) return;
	try {
		localStorage.setItem(USER_EXPLICIT_KEY, "true");
		localStorage.setItem(USER_LANG_KEY, lng);
	} catch {
		// ignore
	}
	await Promise.all([ensureLanguageBundle(lng), syncDayjsLocale(lng)]);
	await i18next.changeLanguage(lng);
}

/**
 * Revert a failed persist — restores prior UI language without clearing an
 * earlier explicit preference when rolling back to it.
 */
export function revertUserLanguage(lng: string): void {
	if (englishLocked) return;
	if (!isSupportedLanguage(lng)) return;
	try {
		localStorage.setItem(USER_LANG_KEY, lng);
		if (isSupportedLanguage(lng)) {
			localStorage.setItem(USER_EXPLICIT_KEY, "true");
		}
	} catch {
		// ignore
	}
	void syncDayjsLocale(lng).then(() => i18next.changeLanguage(lng));
}

/** Whether the user (or backend sync) has already picked a language. */
export function hasExplicitLanguagePreference(): boolean {
	try {
		return localStorage.getItem(USER_EXPLICIT_KEY) === "true";
	} catch {
		return false;
	}
}

export function getStoredLanguage(): SupportedLanguage | undefined {
	try {
		const lng = localStorage.getItem(USER_LANG_KEY);
		return isSupportedLanguage(lng) ? lng : undefined;
	} catch {
		return undefined;
	}
}

export { SUPPORTED_LANGUAGES };
export type { SupportedLanguage };

export default i18next;
